import { Waves, Users, TrendingUp, Shield, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
//import { WaterEffect } from "./WaterEffect";
import { useTheme } from "next-themes";
import { useAuth } from "../hooks/useAuth";

interface LandingPageProps {
  onNavigate: (page: string) => void;
}

export function LandingPage({ onNavigate }: LandingPageProps) {
  const { theme } = useTheme();
  const { clearError } = useAuth();

  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";
  const footerLogoSrc = theme === "dark" ? "/logo.png" : "/logo_white.png";
  
  const navigate = (url: string, page: string) => {
  if (window.location.pathname !== url) {
    window.history.pushState({}, "", url);
  }
  onNavigate(page);
};


  const features = [
    {
      icon: Waves,
      title: "Smart Training",
      description: "AI-powered workout recommendations tailored to your goals"
    },
    {
      icon: Users,
      title: "Expert Coaching",
      description: "Connect with certified swimming coaches for personalized guidance"
    },
    {
      icon: TrendingUp,
      title: "Progress Tracking",
      description: "Monitor your improvement with detailed analytics and insights"
    },
    {
      icon: Shield,
      title: "Safe & Secure",
      description: "Your data is protected with enterprise-grade security"
    }
  ];

  const benefits = [
    "Personalized training plans",
    "Real-time performance analytics",
    "Expert coach matching",
    "Community support",
    "Mobile app access",
    "Progress tracking"
  ];

  return (
    <div className="
  min-h-screen relative overflow-hidden
  bg-gradient-to-br
  from-cyan-50 via-blue-100 to-sky-200
  dark:from-slate-900 dark:via-slate-800 dark:to-slate-900
">

      {/* Swimming Pool Water Effect Background */}
      {/* <WaterEffect className="opacity-50" intensity="high" showLanes={true} /> */}
      
      {/* Header with Glass Effect */}
      <header className="border-b glass-header sticky top-0 z-50 transition-all duration-300">
  <div className="container mx-auto px-6 py-4">
  <div className="flex items-center justify-between">

    <div className="flex items-center">
      <img
        src={logoSrc}
        alt="Swimnerd Logo"
        className="h-12 w-auto cursor-pointer"
        onClick={() => {
          clearError();
          navigate("/", "landing");
        }}
      />
    </div>

    <div className="flex items-center gap-4">

      <Button
        variant="ghost"
        onClick={() => {
          clearError();
          navigate("/find-pool", "find-pool");
        }}
      >
        Find Pool
      </Button>

      <Button
        variant="ghost"
        onClick={() => {
          clearError();
          navigate("/blog", "blog");
        }}
      >
        Blog
      </Button>

      <Button
        variant="ghost"
        onClick={() => {
          clearError();
          navigate("/signin", "signin");
        }}
      >
        Sign In
      </Button>

      <Button
        onClick={() => {
          clearError();
          navigate("/signup", "signup");
        }}
      >
        Get Started
      </Button>

    </div>

  </div>
</div>

</header>


      {/* Hero Section with Glass Morphism - Full Width and Height */}
      <section className="w-full h-screen relative overflow-hidden flex items-center justify-center">
        {/* Background Graphics */}
        <div className="absolute inset-0 -z-10">
          {/* Floating glass orbs */}
          <div className="absolute top-20 left-10 w-20 h-20 glass-subtle rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-16 h-16 glass-subtle rounded-full animate-bounce"></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 glass-subtle rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-60 right-1/3 w-8 h-8 glass-subtle rounded-full animate-bounce" style={{animationDelay: '0.5s'}}></div>
          
          {/* Animated Wave graphics */}
          <div className="absolute bottom-0 left-0 w-full h-40 overflow-hidden">
            <svg className="absolute bottom-0 w-full h-full water-wave" viewBox="0 0 1200 160" preserveAspectRatio="none">
              <path d="M0,80 C300,140 600,20 900,80 C1050,110 1200,50 1200,80 L1200,160 L0,160 Z" fill="url(#waveGradient)" opacity="0.2"/>
              <defs>
                <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#3B82F6"/>
                  <stop offset="100%" stopColor="#1D4ED8"/>
                </linearGradient>
              </defs>
            </svg>
            <svg className="absolute bottom-0 w-full h-full water-wave-slow" viewBox="0 0 1200 160" preserveAspectRatio="none" style={{ animationDelay: '1s' }}>
              <path d="M0,70 C200,130 400,30 600,70 C800,100 1000,40 1200,70 L1200,160 L0,160 Z" fill="url(#waveGradient2)" opacity="0.15"/>
              <defs>
                <linearGradient id="waveGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#60A5FA"/>
                  <stop offset="100%" stopColor="#3B82F6"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        <div className="container mx-auto px-6 text-center relative z-10">
          {/* Hero Illustration with Glass Card */}
          <div className="mb-12 flex justify-center">
            <div className="relative">
              {/* Glass card container */}
              <div className="glass-strong rounded-3xl p-8 shadow-2xl backdrop-blur-xl">
                {/* Main swimming icon with floating animation */}
                <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-2xl water-float mx-auto">
                <Waves className="h-16 w-16 text-white" />
                </div>
              </div>
              
              {/* Floating glass elements around the main icon */}
              <div className="absolute -top-4 -right-4 glass-subtle w-10 h-10 rounded-full flex items-center justify-center animate-bounce shadow-lg">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <div className="absolute -bottom-4 -left-4 glass-subtle w-10 h-10 rounded-full flex items-center justify-center animate-bounce shadow-lg" style={{animationDelay: '0.5s'}}>
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="absolute top-1/2 -left-8 glass-subtle w-8 h-8 rounded-full flex items-center justify-center animate-pulse shadow-lg">
                <Shield className="h-4 w-4 text-blue-600" />
              </div>
            </div>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 animate-fade-in">
            Train Smarter,{" "}
            <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              Swim Faster
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            The ultimate platform for swimmers to track progress, connect with coaches, 
            and achieve their goals with AI-powered insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => {
                clearError();
                navigate("/signup", "signup");
              }} 
              className="text-lg px-8 py-6 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Start Your Journey
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => {
                  clearError();
                  navigate("/signin", "signin");
                }}
              className="text-lg px-8 py-6 glass-subtle hover:scale-105 transition-all duration-300"
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section with Glass Cards */}
      <section className="py-20 bg-white dark:bg-slate-900 relative overflow-hidden">

        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-full -translate-y-48 translate-x-48 opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-blue-50 to-indigo-100 rounded-full translate-y-40 -translate-x-40 opacity-50"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Swimnerd?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to improve your swimming performance in one platform
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="text-center glass-strong hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 hover:scale-105 group border-0"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 glass-strong rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg">
                    <feature.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-700">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section with Glass Morphism */}
      <section className="
          py-20 relative overflow-hidden
          bg-gradient-to-br from-blue-50 to-indigo-100
          dark:from-slate-800 dark:to-slate-900
        ">

        {/* Background graphics */}
        <div className="absolute inset-0 -z-10">
          {/* Swimming lane lines */}
          <div className="absolute top-1/4 left-0 w-full h-1 bg-blue-200 opacity-20"></div>
          <div className="absolute top-1/2 left-0 w-full h-1 bg-blue-200 opacity-20"></div>
          <div className="absolute top-3/4 left-0 w-full h-1 bg-blue-200 opacity-20"></div>
          
          {/* Floating elements */}
          <div className="absolute top-20 right-10 w-6 h-6 bg-blue-300 rounded-full opacity-30 animate-bounce"></div>
          <div className="absolute bottom-20 left-10 w-4 h-4 bg-indigo-300 rounded-full opacity-40 animate-pulse"></div>
          <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-blue-400 rounded-full opacity-25 animate-bounce"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Everything You Need to Succeed
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Join thousands of swimmers who have improved their performance with Swimnerd's 
                comprehensive training platform.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3 group">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:text-center">
              {/* Glass card with swimming illustration */}
              <div className="glass-strong rounded-3xl shadow-2xl p-8 max-w-md mx-auto relative overflow-hidden hover:scale-105 transition-all duration-500">
                {/* Card background decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 glass-subtle rounded-full -translate-y-16 translate-x-16 opacity-60"></div>
                
                {/* Swimming illustration */}
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto shadow-xl water-float">
                    <Waves className="h-10 w-10 text-white" />
                  </div>
                  {/* Ripple effect */}
                  <div className="absolute inset-0 w-20 h-20 glass-subtle rounded-full mx-auto water-ripple"></div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Ready to Get Started?
                </h3>
                <p className="text-gray-700 mb-6">
                  Join our community of swimmers and start your journey to better performance.
                </p>
                <div className="space-y-4">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full glass-subtle hover:scale-105 transition-all duration-300"
                    onClick={() => {
                        clearError();
                        navigate("/signup", "signup");
                      }}>
                    Create Account
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full glass-subtle hover:scale-105 transition-all duration-300"
                    onClick={() => {
                      clearError();
                      navigate("/signin", "signin");
                    }}
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section with Glass Cards */}
      <section className="py-20 relative overflow-hidden">
        {/* Swimming Pool Water Effect */}
        {/* <WaterEffect className="opacity-25" intensity="low" showLanes={false} /> */}
        
        {/* Background decoration with glass */}
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-50/50 to-indigo-50/50"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Swimmers Worldwide
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join thousands of swimmers who have improved their performance with our platform
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center group glass-strong rounded-3xl p-8 hover:scale-105 transition-all duration-300">
              <div className="w-20 h-20 glass-strong rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-xl">
                <Users className="h-10 w-10 text-blue-600" />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">10,000+</div>
              <div className="text-gray-700 font-medium">Active Swimmers</div>
            </div>
            
            <div className="text-center group glass-strong rounded-3xl p-8 hover:scale-105 transition-all duration-300">
              <div className="w-20 h-20 glass-strong rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-xl">
                <TrendingUp className="h-10 w-10 text-green-600" />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">95%</div>
              <div className="text-gray-700 font-medium">Performance Improvement</div>
            </div>
            
            <div className="text-center group glass-strong rounded-3xl p-8 hover:scale-105 transition-all duration-300">
              <div className="w-20 h-20 glass-strong rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-xl">
                <Waves className="h-10 w-10 text-orange-600" />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">50,000+</div>
              <div className="text-gray-700 font-medium">Workouts Completed</div>
            </div>
            
            <div className="text-center group glass-strong rounded-3xl p-8 hover:scale-105 transition-all duration-300">
              <div className="w-20 h-20 glass-strong rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-xl">
                <Shield className="h-10 w-10 text-purple-600" />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">4.9/5</div>
              <div className="text-gray-700 font-medium">User Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with Dark Water Effect */}
      <footer className="bg-gray-900 text-white py-12 relative overflow-hidden">
        {/* Dark Swimming Pool Water Effect */}
        {/* <WaterEffect className="opacity-40" intensity="medium" showLanes={false} darkMode={true} /> */}
        
        {/* Background graphics */}
        <div className="absolute inset-0 -z-10">
          {/* Swimming lane lines with dark glass effect */}
          <div className="absolute top-1/4 left-0 w-full h-1 bg-blue-900/30 opacity-30"></div>
          <div className="absolute top-1/2 left-0 w-full h-1 bg-blue-900/30 opacity-30"></div>
          <div className="absolute top-3/4 left-0 w-full h-1 bg-blue-900/30 opacity-30"></div>
          
          {/* Floating dark glass elements */}
          <div className="absolute top-20 right-10 w-6 h-6 bg-blue-800/40 rounded-full animate-bounce shadow-lg"></div>
          <div className="absolute bottom-20 left-10 w-4 h-4 bg-indigo-800/40 rounded-full animate-pulse shadow-lg"></div>
          <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-blue-700/40 rounded-full animate-bounce shadow-lg"></div>
          
          {/* Subtle wave pattern at bottom */}
          <div className="absolute bottom-0 left-0 w-full h-20 overflow-hidden">
            <svg className="absolute bottom-0 w-full h-full" viewBox="0 0 1200 80" preserveAspectRatio="none">
              <path d="M0,40 C300,80 600,0 900,40 C1050,60 1200,20 1200,40 L1200,80 L0,80 Z" fill="url(#footerWave)" opacity="0.15"/>
              <defs>
                <linearGradient id="footerWave" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#1e40af"/>
                  <stop offset="100%" stopColor="#1e3a8a"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img
                  src={footerLogoSrc}
                  alt="Swimnerd Logo"
                  className="h-8 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-4">
                Unleash Your Speed. The ultimate platform for swimmers.
              </p>
              <div className="flex space-x-4">
                <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <span className="text-sm">f</span>
                </div>
                <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <span className="text-sm">t</span>
                </div>
                <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <span className="text-sm">in</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="hover:text-white transition-colors cursor-pointer">Features</li>
                <li className="hover:text-white transition-colors cursor-pointer">Pricing</li>
                <li className="hover:text-white transition-colors cursor-pointer">Coaches</li>
                <li className="hover:text-white transition-colors cursor-pointer">Community</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => navigate("/help", "help")}
                >
                  Help Center
                </li>

                <li
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => navigate("/", "contact")}
                >
                  Contact Us
                </li>

                <li
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => navigate("/privacy-policy", "privacy-policy")}
                >
                  Privacy Policy
                </li>

                <li
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => navigate("/terms-of-service", "terms-of-service")}
                >
                  Terms of Service
                </li>
              </ul>

            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="hover:text-white transition-colors cursor-pointer">Twitter</li>
                <li className="hover:text-white transition-colors cursor-pointer">Instagram</li>
                <li className="hover:text-white transition-colors cursor-pointer">LinkedIn</li>
                <li className="hover:text-white transition-colors cursor-pointer">YouTube</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy;  {new Date().getFullYear()} Swimnerd. Built for smarter swimming.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
